import { useQuery } from "@tanstack/react-query";
import { Loader2, GitCompare } from "lucide-react";
import ReactDiffViewer, { DiffMethod } from "react-diff-viewer-continued";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";

interface VersionComparisonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  versionIds: string[];
  onClose: () => void;
}

export const VersionComparisonDialog = ({
  open,
  onOpenChange,
  versionIds,
  onClose,
}: VersionComparisonDialogProps) => {
  const { data: versions, isLoading, error } = useQuery({
    queryKey: ["version-comparison", versionIds],
    queryFn: async () => {
      if (versionIds.length !== 2) return null;

      const { data, error } = await supabase
        .from("prompt_versions" as any)
        .select(`
          *,
          prompt_templates:template_id (name, category)
        `)
        .in("id", versionIds);

      if (error) {
        console.error("Error fetching versions for comparison:", error);
        throw error;
      }
      return data as any[];
    },
    enabled: versionIds.length === 2,
    retry: 1,
  });

  const version1 = versions?.[0];
  const version2 = versions?.[1];

  // Format content for diff view
  const getContentString = (content: any) => {
    if (typeof content === 'object') {
      return JSON.stringify(content, null, 2);
    }
    return content || '';
  };

  const diffStyles = {
    variables: {
      dark: {
        diffViewerBackground: 'hsl(var(--background))',
        diffViewerColor: 'hsl(var(--foreground))',
        addedBackground: 'hsl(142 76% 36% / 0.15)',
        addedColor: 'hsl(142 76% 36%)',
        removedBackground: 'hsl(0 84% 60% / 0.15)',
        removedColor: 'hsl(0 84% 60%)',
        wordAddedBackground: 'hsl(142 76% 36% / 0.3)',
        wordRemovedBackground: 'hsl(0 84% 60% / 0.3)',
        addedGutterBackground: 'hsl(142 76% 36% / 0.1)',
        removedGutterBackground: 'hsl(0 84% 60% / 0.1)',
        gutterBackground: 'hsl(var(--muted))',
        gutterBackgroundDark: 'hsl(var(--muted))',
        highlightBackground: 'hsl(var(--accent))',
        highlightGutterBackground: 'hsl(var(--accent))',
      },
    },
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitCompare className="h-5 w-5" />
            Version Comparison
          </DialogTitle>
          <DialogDescription>
            Compare the differences between two prompt versions
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {error && (
          <div className="text-center py-8">
            <p className="text-destructive">Failed to load version comparison</p>
            <p className="text-sm text-muted-foreground mt-2">Please try again</p>
          </div>
        )}

        {!isLoading && !error && versions && versions.length === 2 && (
          <Tabs defaultValue="diff" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="diff">Diff View</TabsTrigger>
              <TabsTrigger value="side-by-side">Side by Side</TabsTrigger>
            </TabsList>

            <TabsContent value="diff" className="mt-4">
              <div className="space-y-6">
                {/* Metadata comparison */}
                <div className="grid grid-cols-2 gap-4">
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">
                          {version1?.prompt_templates?.name}
                        </CardTitle>
                        <div className="flex gap-2">
                          {version1?.is_latest_version && <Badge>Latest</Badge>}
                          {version1?.status === 'active' && <Badge variant="secondary">Active</Badge>}
                          <Badge variant="outline">v{version1?.version}</Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground">Created</h4>
                        <p className="text-sm">{new Date(version1?.created_at).toLocaleString()}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground">Change Notes</h4>
                        <p className="text-sm">{version1?.change_notes || "No notes"}</p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">
                          {version2?.prompt_templates?.name}
                        </CardTitle>
                        <div className="flex gap-2">
                          {version2?.is_latest_version && <Badge>Latest</Badge>}
                          {version2?.status === 'active' && <Badge variant="secondary">Active</Badge>}
                          <Badge variant="outline">v{version2?.version}</Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground">Created</h4>
                        <p className="text-sm">{new Date(version2?.created_at).toLocaleString()}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground">Change Notes</h4>
                        <p className="text-sm">{version2?.change_notes || "No notes"}</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Content Diff */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Content Differences</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[400px] w-full">
                      <ReactDiffViewer
                        oldValue={getContentString(version1?.content)}
                        newValue={getContentString(version2?.content)}
                        splitView={true}
                        compareMethod={DiffMethod.WORDS}
                        styles={diffStyles}
                        leftTitle={`Version ${version1?.version}`}
                        rightTitle={`Version ${version2?.version}`}
                        useDarkTheme={true}
                        showDiffOnly={false}
                      />
                    </ScrollArea>
                  </CardContent>
                </Card>

                {/* Model Config Diff */}
                {(version1?.model_config || version2?.model_config) && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Model Configuration Differences</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ScrollArea className="h-[300px] w-full">
                        <ReactDiffViewer
                          oldValue={version1?.model_config ? JSON.stringify(version1.model_config, null, 2) : ''}
                          newValue={version2?.model_config ? JSON.stringify(version2.model_config, null, 2) : ''}
                          splitView={true}
                          compareMethod={DiffMethod.LINES}
                          styles={diffStyles}
                          leftTitle={`Version ${version1?.version} Config`}
                          rightTitle={`Version ${version2?.version} Config`}
                          useDarkTheme={true}
                          showDiffOnly={false}
                        />
                      </ScrollArea>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            <TabsContent value="side-by-side" className="mt-4">
              <div className="grid grid-cols-2 gap-4">
                {/* Version 1 */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">
                        {version1?.prompt_templates?.name}
                      </CardTitle>
                      <div className="flex gap-2">
                        {version1?.is_latest_version && <Badge>Latest</Badge>}
                        {version1?.status === 'active' && <Badge variant="secondary">Active</Badge>}
                        <Badge variant="outline">v{version1?.version}</Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <h4 className="text-sm font-medium mb-2 text-muted-foreground">Created</h4>
                      <p className="text-sm">
                        {new Date(version1?.created_at).toLocaleString()}
                      </p>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium mb-2 text-muted-foreground">Status</h4>
                      <p className="text-sm capitalize">{version1?.status || "Unknown"}</p>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium mb-2 text-muted-foreground">Change Notes</h4>
                      <p className="text-sm">{version1?.change_notes || "No notes"}</p>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium mb-2 text-muted-foreground">Content</h4>
                      <ScrollArea className="h-[300px] w-full rounded-md border p-4">
                        <pre className="text-xs font-mono whitespace-pre-wrap">
                          {typeof version1?.content === 'object' 
                            ? JSON.stringify(version1.content, null, 2)
                            : version1?.content || 'No content'}
                        </pre>
                      </ScrollArea>
                    </div>

                    {version1?.model_config && (
                      <div>
                        <h4 className="text-sm font-medium mb-2 text-muted-foreground">Model Configuration</h4>
                        <ScrollArea className="h-[150px] w-full rounded-md border p-4">
                          <pre className="text-xs font-mono whitespace-pre-wrap">
                            {JSON.stringify(version1.model_config, null, 2)}
                          </pre>
                        </ScrollArea>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Version 2 */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">
                        {version2?.prompt_templates?.name}
                      </CardTitle>
                      <div className="flex gap-2">
                        {version2?.is_latest_version && <Badge>Latest</Badge>}
                        {version2?.status === 'active' && <Badge variant="secondary">Active</Badge>}
                        <Badge variant="outline">v{version2?.version}</Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <h4 className="text-sm font-medium mb-2 text-muted-foreground">Created</h4>
                      <p className="text-sm">
                        {new Date(version2?.created_at).toLocaleString()}
                      </p>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium mb-2 text-muted-foreground">Status</h4>
                      <p className="text-sm capitalize">{version2?.status || "Unknown"}</p>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium mb-2 text-muted-foreground">Change Notes</h4>
                      <p className="text-sm">{version2?.change_notes || "No notes"}</p>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium mb-2 text-muted-foreground">Content</h4>
                      <ScrollArea className="h-[300px] w-full rounded-md border p-4">
                        <pre className="text-xs font-mono whitespace-pre-wrap">
                          {typeof version2?.content === 'object' 
                            ? JSON.stringify(version2.content, null, 2)
                            : version2?.content || 'No content'}
                        </pre>
                      </ScrollArea>
                    </div>

                    {version2?.model_config && (
                      <div>
                        <h4 className="text-sm font-medium mb-2 text-muted-foreground">Model Configuration</h4>
                        <ScrollArea className="h-[150px] w-full rounded-md border p-4">
                          <pre className="text-xs font-mono whitespace-pre-wrap">
                            {JSON.stringify(version2.model_config, null, 2)}
                          </pre>
                        </ScrollArea>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        )}

        <div className="flex justify-end">
          <Button onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
